import { useEffect, useMemo, useState } from "react";
import { filterSlug } from "../../shared/filterSlug.js";
import { useLocation, useNavigate } from "react-router-dom";
import { apiDownload } from "../api/client.js";
import { useRaw } from "../api/hooks.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import { P } from "../../shared/permissions.js";
import { DataTable } from "./DataTable.jsx";
import { Card, ErrorState, SectionTitle, Skeleton } from "./primitives.jsx";
import { Button, Checkbox, Input } from "./ui/index.js";
import { useFilters } from "../lib/useFilters.js";
import { fmt } from "../lib/format.js";

const PAGE_SIZE = 50;

/**
 * Row-level table served by /api/raw/:page: server-side search, sort and
 * paging, CSV export, and phone numbers masked unless revealed. Rows carry
 * personal data, so the API only serves them to the internal role.
 */
export function RowTable({ page, title, extra, onClear, id }) {
  const f = useFilters();
  const navigate = useNavigate();
  const { search: locationSearch } = useLocation();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState(null);
  const [reveal, setReveal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // A new page, filter or search starts from the first page.
  const extraKey = JSON.stringify(extra ?? {});
  useEffect(() => setOffset(0), [page, f.apiQuery, query, sort, extraKey]);

  const qs = useMemo(() => {
    const q = new URLSearchParams(f.apiQuery);
    q.set("limit", String(PAGE_SIZE));
    q.set("offset", String(offset));
    if (query) q.set("q", query);
    if (sort) {
      q.set("sort", sort.key);
      q.set("dir", sort.dir);
    }
    if (reveal) q.set("reveal", "1");
    for (const [k, v] of Object.entries(extra ?? {})) q.set(k, v);
    return q.toString();
  }, [f.apiQuery, offset, query, sort, reveal, extra]);

  const { data, error, isLoading, isFetching } = useRaw(page, qs);

  const total = data?.total ?? 0;
  const { can } = useAuth();
  const hasPhone = data?.columns.some((c) => c.phone) && can(P.pii);

  const download = async () => {
    setDownloading(true);
    setDownloadError("");
    const q = new URLSearchParams(qs);
    q.delete("limit");
    q.delete("offset");
    q.set("format", "csv");
    try {
      const set = Object.fromEntries(f.pageKeys.filter((k) => f.filters[k]).map((k) => [k, f.filters[k]]));
      await apiDownload(`/raw/${page}?${q}`, `${f.filters.tenant}_${page}_${f.filters.from}_${f.filters.to}${filterSlug(set)}.csv`);
    } catch (err) {
      setDownloadError(err.message);
    } finally {
      setDownloading(false);
    }
  };


  return (
    <div id={id} className="col-span-12 scroll-mt-16">
    <Card>
      <SectionTitle
        hint={data?.description}
        actions={
          <>
            {onClear && (
              <Button size="xs" onClick={onClear}>
                Show all sessions
              </Button>
            )}
            <span className="tabular mx-2 text-[11px] text-ink-3">{isFetching ? "Loading…" : `${fmt(total)} rows`}</span>
            {can(P.export) && (
              <Button size="xs" icon="download" onClick={download} loading={downloading} disabled={!total}>
                {downloading ? "Preparing…" : "Download CSV"}
              </Button>
            )}
          </>
        }
      >
        {title ?? data?.title ?? page}
      </SectionTitle>
      {error && <ErrorState what="these rows" message={error.message} retryable={error.retryable !== false} />}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Input
          size="sm"
          icon="search"
          type="search"
          className="w-72"
          placeholder="Search rows…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search rows"
        />
        {hasPhone && (
          <Checkbox label="Show full phone numbers" checked={reveal} onChange={(e) => setReveal(e.target.checked)} className="text-[11.5px]" />
        )}
        {downloadError && <span className="text-[11px] text-critical">{downloadError}</span>}
      </div>
      {isLoading && <Skeleton height={420} />}
      {data && (
        <div className={isFetching ? "opacity-60" : ""}>
          <DataTable
            columns={data.columns}
            rows={data.rows}
            maxHeight={640}
            dense
            sort={{ key: data.sort, dir: data.dir.toLowerCase() }}
            onSort={setSort}
            onRowClick={
              data.link?.to === "user" ? (row) => row[data.link.idKey] && navigate({ pathname: `/user/${row[data.link.idKey]}`, search: locationSearch }) : undefined
            }
          />
        </div>
      )}
      {data && total > PAGE_SIZE && (
        <div className="mt-3 flex items-center justify-end gap-2">
          <span className="tabular text-[11px] text-ink-3">
            {fmt(offset + 1)}–{fmt(Math.min(offset + PAGE_SIZE, total))} of {fmt(total)}
          </span>
          <Button size="xs" icon="chevronLeft" disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}>
            Previous
          </Button>
          <Button size="xs" iconRight="chevronRight" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset((o) => o + PAGE_SIZE)}>
            Next
          </Button>
        </div>
      )}
    </Card>
    </div>
  );
}
